<template>
  <div class="w-full cursor-pointer" @click="toggleExpanded">
    <div
      class="w-full border border-gray-2 rounded-lg mayday-ai-gradient-light text-body"
    >
      <div class="flex p-2 px-3 items-center justify-between space-x-2 w-full">
        <div class="flex items-center space-x-2 text-sm">
          <FontAwesomeIcon icon="ticket" />
          <div>
            <div>{{ $t('genius.automations.toast-title') }}</div>
            <div
              v-if="!!manualAutomations.length"
              class="text-xs text-caption overflow-hidden transition-all"
            >
              {{
                $t('genius.automations.toast-description', {
                  count: manualAutomations.length,
                })
              }}
            </div>
          </div>
        </div>
        <div class="flex items-center space-x-2">
          <BaseButton
            v-if="displayGenerateCta"
            variant="secondary"
            :text="$t('genius.automations.toast-cta')"
            @click="handleClickCTA"
          />
          <FontAwesomeIcon
            v-if="manualAutomationsLoading || manualAutomations?.length"
            :icon="toastIcon.icon"
            :spin="toastIcon.spin"
            class="transition-all"
            :class="expanded ? 'rotate-90' : 'rotate-0'"
          />
        </div>
      </div>
      <div
        class="transition-all overflow-y-auto px-2 border-gray-2"
        :class="expanded ? 'max-h-[300px] py-2 border-t' : 'max-h-0 py-0'"
        @click.stop
      >
        <CopilotAutomations
          :manual-automations-loading="manualAutomationsLoading"
          :manual-automations="manualAutomations"
          :lang="selectedLanguage || locale"
          @go-to-content="handleGoToContent"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  ExternalContent,
  useAdminStore,
  useCopilotStore,
  useKnowledgeStore,
} from '@getmayday/vue-modules';
import type { LightEntity } from '@getmayday/vue-modules';
import { useAgentStore } from '@getmayday/vue-modules/agent';
import CopilotAutomations from './CopilotAutomations.vue';
import { externalContentUri } from '../../utils/externalContentsUri';
import { useConfigStore } from '../../stores/config';
import { useRouter } from 'vue-router';

const copilotStore = useCopilotStore();
const { manualAutomations, manualAutomationsLoading } =
  storeToRefs(copilotStore);

const configStore = useConfigStore();
const { locale, currentTabContext } = storeToRefs(configStore);

const knowledgeStore = useKnowledgeStore();
const { selectedLanguage } = storeToRefs(knowledgeStore);

const { goToEntity } = useEntities();

const router = useRouter();

const props = defineProps<{
  hasCopilotV2: boolean;
}>();

const expanded = ref(true);

const displayGenerateCta = computed(() => {
  return !manualAutomationsLoading.value && props.hasCopilotV2;
});

const toggleExpanded = () => {
  if (manualAutomations.value?.length || manualAutomationsLoading.value) {
    expanded.value = !expanded.value;
  }
  copilotStore.updateHasUserOpenedCopilot(true);
};

const handleClickCTA = () => {
  if (!currentTabContext.value?.conversationId) return;
  copilotStore.logTriggerTicketAnalysis(
    currentTabContext.value.conversationId,
    currentTabContext.value.tabSource,
    'toast',
  );
  router.push('/copilot?trigger=true');
};

const handleGoToContent = (content: LightEntity | ExternalContent) => {
  if (content.hasOwnProperty('type')) {
    goToEntity({
      ...(content as LightEntity),
      fromModal: true,
    });
  } else {
    window.open(externalContentUri(content as ExternalContent), '_blank');
  }
};

const toastIcon = computed(() => {
  if (!manualAutomationsLoading.value) {
    return {
      icon: 'chevron-right',
      spin: false,
    };
  }
  return {
    icon: 'spinner-third',
    spin: true,
  };
});

watch(manualAutomationsLoading, (value, oldValue) => {
  if (!value && oldValue) {
    expanded.value = !!manualAutomations.value?.length;
  }
});
</script>

<style scoped></style>
